import type { ClientFrame, ServerFrame } from './types'

export type SocketState = 'connecting' | 'open' | 'closed'

export interface SocketOptions {
  /** Base HTTP URL ('' = same origin as the page). */
  baseUrl: string
  token: () => string | null
  onFrame: (f: ServerFrame) => void
  onState?: (s: SocketState) => void
  /** Called after every (re)connect so the app can re-sync anything it missed. */
  onReady?: () => void
  pingMs?: number
}

/** After this long without a frame, coming back to the page or the network checks the socket. */
const PROBE_MS = 3_000

/**
 * A notification socket that authenticates with its first frame and reconnects with
 * capped exponential backoff. Notifications are hints: the app pulls data over HTTP,
 * so anything missed while disconnected is recovered by the onReady re-sync.
 *
 * Sockets can die without a close event (a phone's network changing, an app frozen in the
 * background until the server gave up on it), so it also watches for silence: a ping
 * unanswered by the next one, or nothing heard soon after the page comes back or the
 * network returns, means dead, and it reconnects at once rather than after a backoff
 * (HRTH-201).
 */
export class NotificationSocket {
  private ws: WebSocket | null = null
  private attempts = 0
  private stopped = false
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private probeTimer: ReturnType<typeof setTimeout> | null = null
  private lastHeard = 0

  constructor(private readonly opts: SocketOptions) {}

  private url(): string {
    const base = this.opts.baseUrl || globalThis.location.origin
    return `${base.replace(/^http/, 'ws')}/api/v1/ws`
  }

  start(): void {
    this.stopped = false
    globalThis.document?.addEventListener('visibilitychange', this.onVisibility)
    globalThis.addEventListener?.('online', this.onOnline)
    this.connect()
  }

  stop(): void {
    this.stopped = true
    globalThis.document?.removeEventListener('visibilitychange', this.onVisibility)
    globalThis.removeEventListener?.('online', this.onOnline)
    this.clearTimers()
    this.ws?.close()
    this.ws = null
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    if (this.probeTimer) clearTimeout(this.probeTimer)
    this.retryTimer = this.pingTimer = this.probeTimer = null
  }

  /** The server pushes notifications only to devices whose app isn't on screen. */
  private readonly onVisibility = (): void => {
    const visible = globalThis.document?.visibilityState === 'visible'
    this.send({ t: 'visible', visible })
    if (visible) this.check()
  }

  private readonly onOnline = (): void => this.check()

  /** Reconnects now if the socket is down; probes it if it has been quiet. */
  private check(): void {
    if (this.stopped) return
    if (!this.ws) return this.reconnect()
    if (this.probeTimer || Date.now() - this.lastHeard < (this.opts.pingMs ?? 25_000)) return
    this.send({ t: 'ping' })
    const asked = Date.now()
    this.probeTimer = setTimeout(() => {
      this.probeTimer = null
      if (this.lastHeard < asked) this.reconnect()
    }, PROBE_MS)
  }

  /** Drops the current socket (if any) and connects again without waiting out a backoff. */
  private reconnect(): void {
    const old = this.ws
    this.ws = null
    this.clearTimers()
    old?.close()
    this.attempts = 0
    this.connect()
  }

  private send(f: ClientFrame): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(f))
  }

  private connect(): void {
    const token = this.opts.token()
    if (!token || this.stopped) return
    this.opts.onState?.('connecting')
    const ws = new WebSocket(this.url())
    this.ws = ws
    ws.onopen = () => ws.send(JSON.stringify({ t: 'auth', token } satisfies ClientFrame))
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return
      this.lastHeard = Date.now()
      let frame: ServerFrame
      try {
        frame = JSON.parse(String(ev.data)) as ServerFrame
      } catch {
        return
      }
      if (frame.t === 'ready') {
        this.attempts = 0
        this.opts.onState?.('open')
        if (this.pingTimer) clearInterval(this.pingTimer)
        const pingMs = this.opts.pingMs ?? 25_000
        this.pingTimer = setInterval(() => {
          // The last ping's pong should be in long before this one is due.
          if (Date.now() - this.lastHeard > pingMs * 1.5) return this.reconnect()
          this.send({ t: 'ping' })
        }, pingMs)
        this.onVisibility()
        this.opts.onReady?.()
        return
      }
      this.opts.onFrame(frame)
    }
    ws.onclose = () => {
      if (this.ws !== ws) return
      this.ws = null
      this.clearTimers()
      this.opts.onState?.('closed')
      if (this.stopped) return
      const delay = Math.min(30_000, 500 * 2 ** this.attempts) * (0.75 + Math.random() * 0.5)
      this.attempts++
      this.retryTimer = setTimeout(() => this.connect(), delay)
    }
    ws.onerror = () => ws.close()
  }
}
